import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, FileText, Package, TrendingUp, Wrench, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import StatCard from '@/components/StatCard';
import api from '@/lib/api';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MESES_CURTO = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function Dashboard() {
  const [metrics, setMetrics] = useState({
    vendas_mes: 0,
    orcamentos_pendentes: 0,
    estoque_baixo: 0,
    contas_receber: 0,
    ordens_andamento: 0
  });
  const [loading, setLoading] = useState(true);
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth() + 1);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    loadMetrics();
    loadChartData();
  }, [mesSelecionado, anoSelecionado]);

  const loadMetrics = async () => {
    try {
      const { data } = await api.get(`/dashboard/metrics?mes=${mesSelecionado}&ano=${anoSelecionado}`);
      setMetrics(data);
    } catch (error) {
      toast.error('Erro ao carregar métricas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadChartData = async () => {
    try {
      // Carregar dados dos últimos 6 meses
      const meses = [];
      let mes = mesSelecionado;
      let ano = anoSelecionado;
      
      for (let i = 0; i < 6; i++) {
        meses.unshift({ mes, ano });
        mes--;
        if (mes === 0) {
          mes = 12;
          ano--;
        }
      }
      
      const promises = meses.map(m => 
        api.get(`/financeiro/relatorio?mes=${m.mes}&ano=${m.ano}`)
          .then(res => ({
            name: `${MESES_CURTO[m.mes - 1]}/${String(m.ano).slice(2)}`,
            vendas: res.data.total_entradas || 0,
            despesas: res.data.total_saidas || 0
          }))
          .catch(() => ({
            name: `${MESES_CURTO[m.mes - 1]}/${String(m.ano).slice(2)}`,
            vendas: 0,
            despesas: 0
          }))
      );
      
      const data = await Promise.all(promises);
      setChartData(data);
    } catch (error) {
      console.error('Erro ao carregar gráfico:', error);
    }
  };

  const handleMesAnterior = () => {
    if (mesSelecionado === 1) {
      setMesSelecionado(12);
      setAnoSelecionado(anoSelecionado - 1);
    } else {
      setMesSelecionado(mesSelecionado - 1);
    }
  };

  const handleMesProximo = () => {
    if (mesSelecionado === 12) {
      setMesSelecionado(1);
      setAnoSelecionado(anoSelecionado + 1);
    } else {
      setMesSelecionado(mesSelecionado + 1);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-400">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-1 sm:mb-2">Dashboard</h1>
          <p className="text-sm sm:text-base text-slate-500">Visão geral da sua oficina</p>
        </div>
        {/* Seletor de Mês */}
        <div className="flex items-center gap-2 bg-carbon border border-white/10 rounded-md px-2 py-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMesAnterior}
            className="text-slate-400 hover:text-white p-1"
            data-testid="dash-mes-anterior-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-white font-medium text-sm min-w-[120px] text-center">
            {MESES[mesSelecionado - 1]} {anoSelecionado}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMesProximo}
            className="text-slate-400 hover:text-white p-1"
            data-testid="dash-mes-proximo-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
        <StatCard
          title={`Vendas - ${MESES[mesSelecionado - 1]}`}
          value={formatCurrency(metrics.vendas_mes)}
          icon={DollarSign}
        />
        <StatCard
          title="Orçamentos Pendentes"
          value={metrics.orcamentos_pendentes}
          icon={FileText}
        />
        <StatCard
          title="Estoque Baixo"
          value={metrics.estoque_baixo}
          icon={AlertTriangle}
        />
        <StatCard
          title="Contas a Receber"
          value={formatCurrency(metrics.contas_receber)}
          icon={TrendingUp}
        />
        <StatCard
          title="Ordens em Andamento"
          value={metrics.ordens_andamento}
          icon={Wrench}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-carbon border border-white/5 rounded-md p-4 sm:p-6 shadow-lg"
        >
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 uppercase tracking-wide">Vendas - Últimos 6 Meses</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D333E" />
              <XAxis dataKey="name" stroke="#64748B" tick={{ fontSize: 12 }} />
              <YAxis stroke="#64748B" tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#16191E',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#F8FAFC'
                }}
                formatter={(value) => formatCurrency(value)}
              />
              <Bar dataKey="vendas" fill="#22C55E" radius={[4, 4, 0, 0]} name="Entradas" />
              <Bar dataKey="despesas" fill="#EF4444" radius={[4, 4, 0, 0]} name="Saídas" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-carbon border border-white/5 rounded-md p-4 sm:p-6 shadow-lg"
        >
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 uppercase tracking-wide">Tendência de Crescimento</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D333E" />
              <XAxis dataKey="name" stroke="#64748B" tick={{ fontSize: 12 }} />
              <YAxis stroke="#64748B" tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#16191E',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  color: '#F8FAFC'
                }}
                formatter={(value) => formatCurrency(value)}
              />
              <Line type="monotone" dataKey="vendas" stroke="#22C55E" strokeWidth={3} dot={{ fill: '#22C55E' }} name="Entradas" />
              <Line type="monotone" dataKey="despesas" stroke="#EF4444" strokeWidth={3} dot={{ fill: '#EF4444' }} name="Saídas" />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    </div>
  );
}

export default Dashboard;
